<script setup>
import { onMounted, computed, ref, toRefs } from 'vue';
import { useTimeoutFn } from '@vueuse/core';
import { provideMessageContext } from './provider.js';
import { useNhanGiu } from 'dashboard/composables/useNhanGiu';
import { useTrack } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { LocalStorage } from 'shared/helpers/localStorage';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { getInboxIconByType, INBOX_TYPES } from 'dashboard/helper/inbox';
import { useAlert } from 'dashboard/composables';
import MessageApi from 'dashboard/api/inbox/message';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { dateFormat } from 'shared/helpers/timeHelper';
import {
  MESSAGE_TYPES,
  ATTACHMENT_TYPES,
  MESSAGE_VARIANTS,
  SENDER_TYPES,
  ORIENTATION,
  MESSAGE_STATUS,
  CONTENT_TYPES,
} from './constants';

import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'next/icon/Icon.vue';

import TextBubble from './bubbles/Text/Index.vue';
import ActivityBubble from './bubbles/Activity.vue';
import ImageBubble from './bubbles/Image.vue';
import FileBubble from './bubbles/File.vue';
import AudioBubble from './bubbles/Audio.vue';
import VideoBubble from './bubbles/Video.vue';
import EmbedBubble from './bubbles/Embed.vue';
import FallbackBubble from './bubbles/Fallback.vue';
import InstagramStoryBubble from './bubbles/InstagramStory.vue';
import EmailBubble from './bubbles/Email/Index.vue';
import UnsupportedBubble from './bubbles/Unsupported.vue';
import ContactBubble from './bubbles/Contact.vue';
import DyteBubble from './bubbles/Dyte.vue';
import LocationBubble from './bubbles/Location.vue';
import CSATBubble from './bubbles/CSAT.vue';
import FormBubble from './bubbles/Form.vue';
import VoiceCallBubble from './bubbles/VoiceCall.vue';
// BonBoZ: thẻ quảng cáo Click-to-Messenger, không bubble upstream nào hiển thị được.
import AdCardBubble from './bubbles/AdCard.vue';
// BonBoZ: thẻ generic Messenger gửi (opt-in "Ưu đãi và thông báo", link có ảnh).
import FbTemplateCardBubble from './bubbles/FbTemplateCard.vue';
// BonBoZ: thẻ kết bạn / huỷ kết bạn Zalo (message activity do Rails tạo).
import ZaloKetBanCard from './bubbles/ZaloKetBanCard.vue';
import ZaloHuyKetBanCard from './bubbles/ZaloHuyKetBanCard.vue';
// BonBoZ: thẻ cuộc gọi Zalo (cùng kiểu activity do Rails tạo/cập nhật).
import ZaloCuocGoiCard from './bubbles/ZaloCuocGoiCard.vue';

import MessageError from './MessageError.vue';
// BonBoZ: thanh trả lời / chuyển tiếp / cảm xúc hiện khi rê chuột.
import ThanhCongCuTin from './ThanhCongCuTin.vue';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';
// BonBoZ: cỡ avatar là prop số nên phải tính bằng JS, không có class Tailwind nào thay được.
import { usePancakeMetrics } from 'dashboard/composables/usePancakeMetrics';
// BonBoZ: chú thích của cầu nối Zalo giấu trong thân tin, phải bóc ra để vẽ ngoài bong bóng.
import { parseZaloMeta } from './zaloMeta';

/**
 * @typedef {Object} Attachment
 * @property {number} id - Unique identifier for the attachment
 * @property {number} messageId - ID of the associated message
 * @property {'image'|'audio'|'video'|'file'|'location'|'fallback'|'share'|'story_mention'|'contact'|'ig_reel'} fileType - Type of the attachment (file or image)
 * @property {number} accountId - ID of the associated account
 * @property {string|null} extension - File extension
 * @property {string} dataUrl - URL to access the full attachment data
 * @property {string} thumbUrl - URL to access the thumbnail version
 * @property {number} fileSize - Size of the file in bytes
 * @property {number|null} width - Width of the image if applicable
 * @property {number|null} height - Height of the image if applicable
 */

/**
 * @typedef {Object} Sender
 * @property {Object} additional_attributes - Additional attributes of the sender
 * @property {Object} custom_attributes - Custom attributes of the sender
 * @property {string} email - Email of the sender
 * @property {number} id - ID of the sender
 * @property {string|null} identifier - Identifier of the sender
 * @property {string} name - Name of the sender
 * @property {string|null} phone_number - Phone number of the sender
 * @property {string} thumbnail - Thumbnail URL of the sender
 * @property {string} type - Type of sender
 */

/**
 * @typedef {Object} ContentAttributes
 * @property {string} externalError - an error message to be shown if the message failed to send
 */

/**
 * @typedef {Object} Props
 * @property {('sent'|'delivered'|'read'|'failed'|'progress')} status - The delivery status of the message
 * @property {ContentAttributes} [contentAttributes={}] - Additional attributes of the message content
 * @property {Attachment[]} [attachments=[]] - The attachments associated with the message
 * @property {Sender|null} [sender=null] - The sender information
 * @property {boolean} [private=false] - Whether the message is private
 * @property {number|null} [senderId=null] - The ID of the sender
 * @property {number} createdAt - Timestamp when the message was created
 * @property {number} currentUserId - The ID of the current user
 * @property {number} id - The unique identifier for the message
 * @property {number} messageType - The type of message (must be one of MESSAGE_TYPES)
 * @property {string|null} [error=null] - Error message if the message failed to send
 * @property {string|null} [senderType=null] - The type of the sender
 * @property {string} content - The message content
 * @property {boolean} [groupWithNext=false] - Whether the message should be grouped with the next message
 * @property {Object|null} [inReplyTo=null] - The message to which this message is a reply
 * @property {boolean} [isEmailInbox=false] - Whether the message is from an email inbox
 * @property {number} conversationId - The ID of the conversation to which the message belongs
 * @property {number} inboxId - The ID of the inbox to which the message belongs
 */

// eslint-disable-next-line vue/define-macros-order
const props = defineProps({
  id: { type: Number, required: true },
  messageType: {
    type: Number,
    required: true,
    validator: value => Object.values(MESSAGE_TYPES).includes(value),
  },
  status: {
    type: String,
    required: true,
    validator: value => Object.values(MESSAGE_STATUS).includes(value),
  },
  attachments: { type: Array, default: () => [] },
  call: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  content: { type: String, default: null },
  contentAttributes: { type: Object, default: () => ({}) },
  contentType: {
    type: String,
    default: 'text',
    validator: value => Object.values(CONTENT_TYPES).includes(value),
  },
  conversationId: { type: Number, required: true },
  createdAt: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  currentUserId: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  groupWithNext: { type: Boolean, default: false },
  // BonBoZ: cảm xúc CŨ của Zalo, đọc ngược từ ghi chú nội bộ — xem zaloReaction.js.
  // Chỉ còn dùng cho tin từ trước khi có bảng `bonboz_cam_xuc_tins`.
  zaloReactions: { type: Array, default: () => [] },
  // BonBoZ: cảm xúc lưu có cấu trúc, nguồn chính thức từ nay.
  bonbozCamXuc: { type: Array, default: () => [] },
  inboxId: { type: Number, default: null }, // eslint-disable-line vue/no-unused-properties
  inboxSupportsReplyTo: { type: Object, default: () => ({}) },
  inReplyTo: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  isEmailInbox: { type: Boolean, default: false },
  // BonBoZ: chỉ MessageList biết tin nào là tin gửi đi cuối cùng của hội thoại.
  isLastOutgoing: { type: Boolean, default: false },
  // BonBoZ: tin này là mốc đọc của khách — tin đi cuối cùng mà họ đã xem.
  isReadWatermark: { type: Boolean, default: false },
  // BonBoZ: { name, src } của KHÁCH, để vẽ avatar ở mốc đọc.
  readBy: { type: Object, default: null },
  private: { type: Boolean, default: false },
  additionalAttributes: { type: Object, default: () => ({}) }, // eslint-disable-line vue/no-unused-properties
  sender: { type: Object, default: null },
  senderId: { type: Number, default: null },
  senderType: { type: String, default: null },
  sourceId: { type: String, default: '' }, // eslint-disable-line vue/no-unused-properties
});

const emit = defineEmits(['retry']);

const contextMenuPosition = ref({});
const showBackgroundHighlight = ref(false);
const showContextMenu = ref(false);
const { t } = useI18n();
const route = useRoute();
// BonBoZ: `inboxes/getInbox` tra BAN GHI THO cua store, tuc la snake_case —
// doc `channelType` o day luon ra `undefined`. Getter `getAllInboxes` ngay canh
// no moi co `camelcaseKeys`, nen hai getter cung module lai khac dang tra ve.
// Da dinh bay nay hai lan (issue #167).
const inboxGetter = useMapGetter('inboxes/getInbox');
const inbox = computed(() => inboxGetter.value(props.inboxId) || {});
const isOnChatwootCloud = useMapGetter('globalConfig/isOnChatwootCloud');
const { messageAvatarSize } = usePancakeMetrics();

// BonBoZ: bong bóng Facebook là bản gốc — trong bong bóng chỉ có lời người ta
// nói, còn ai nói và gửi từ đâu nằm ở ngoài. Tin Zalo mang hai thứ đó lẫn trong
// thân tin nên phải tách ra trước khi vẽ.
const zaloMeta = computed(() =>
  parseZaloMeta(props.content, inbox.value.channel_type)
);

// Tin mới mang tên người nói ở content_attributes, tin cũ giấu trong thân tin.
const speakerName = computed(
  () => props.contentAttributes?.bonbozSenderName || zaloMeta.value.speaker
);

// Thân tin đã sạch chú thích — đây mới là thứ bong bóng vẽ ra.
const messageContent = computed(() => {
  const { body } = zaloMeta.value;
  if (body) return body;

  // Bóc xong không còn chữ: có đính kèm thì để rỗng cho bong bóng ảnh/tệp nhận
  // tin; không có gì thì giữ nguyên thân tin cũ, một bong bóng rỗng còn tệ hơn
  // một dòng chú thích.
  return props.attachments?.length ? body : (props.content ?? '');
});

const isCaptainMessage = computed(() => {
  const senderType = props.sender?.type ?? props.senderType;
  return senderType === SENDER_TYPES.CAPTAIN_ASSISTANT;
});

/**
 * Computes the message variant based on props
 * @type {import('vue').ComputedRef<'user'|'agent'|'activity'|'private'|'bot'|'template'>}
 */
const variant = computed(() => {
  if (props.private) return MESSAGE_VARIANTS.PRIVATE;

  if (props.isEmailInbox) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) {
      return MESSAGE_VARIANTS.EMAIL;
    }
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return MESSAGE_VARIANTS.EMAIL;
  }

  if (props.status === MESSAGE_STATUS.FAILED) return MESSAGE_VARIANTS.ERROR;
  if (props.contentAttributes?.isUnsupported)
    return MESSAGE_VARIANTS.UNSUPPORTED;

  if (props.contentAttributes?.externalEcho) {
    return MESSAGE_VARIANTS.AGENT;
  }

  // BonBoZ: bot la BOT theo DU LIEU, khong doan. Truoc day tin outgoing nao
  // thieu sender cung bi ve nhu bot, nen nguoi truc khong tin duoc mau bong
  // bong nua. Hai can cu that:
  // - `sender_type='AgentBot'`: bot gui qua API Chatwoot bang token AgentBot.
  // - `content_attributes.bonboz_bot`: bot gui THANG qua Meta API roi tu ghi
  //   lai (scripts/03_recruitment_flow_test.rb#record_in_chatwoot) — ban ghi
  //   do khong co sender nen phai tu xung.
  const isBot =
    props.sender?.type === SENDER_TYPES.AGENT_BOT ||
    props.senderType === SENDER_TYPES.AGENT_BOT ||
    Boolean(props.contentAttributes?.bonbozBot);
  if (isBot && props.messageType === MESSAGE_TYPES.OUTGOING) {
    return MESSAGE_VARIANTS.BOT;
  }

  const variants = {
    [MESSAGE_TYPES.INCOMING]: MESSAGE_VARIANTS.USER,
    [MESSAGE_TYPES.ACTIVITY]: MESSAGE_VARIANTS.ACTIVITY,
    [MESSAGE_TYPES.OUTGOING]: MESSAGE_VARIANTS.AGENT,
    [MESSAGE_TYPES.TEMPLATE]: MESSAGE_VARIANTS.TEMPLATE,
  };

  return variants[props.messageType] || MESSAGE_VARIANTS.USER;
});

const isBotOrAgentMessage = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) {
    return false;
  }
  // if an outgoing message is still processing, then it's definitely a
  // message sent by the current user
  if (
    props.status === MESSAGE_STATUS.PROGRESS &&
    props.messageType === MESSAGE_TYPES.OUTGOING
  ) {
    return true;
  }
  const senderId = props.senderId ?? props.sender?.id;
  const senderType = props.sender?.type ?? props.senderType;

  if (!senderType || !senderId) {
    return true;
  }

  if (
    [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
      senderType
    )
  ) {
    return true;
  }

  return senderType.toLowerCase() === SENDER_TYPES.USER.toLowerCase();
});

/**
 * Computes the message orientation based on sender type and message type
 * @returns {import('vue').ComputedRef<'left'|'right'|'center'>} The computed orientation
 */
const orientation = computed(() => {
  if (isBotOrAgentMessage.value) {
    return ORIENTATION.RIGHT;
  }

  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return ORIENTATION.CENTER;

  return ORIENTATION.LEFT;
});

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

// BonBoZ: Pancake vẽ avatar bên khách, Chatwoot vẽ bên agent. Cột 40px + gap-x-2 = lề 48px.
// `minmax(0,1fr)` chu khong phai `1fr`: mac dinh mot track grid khong co ngot
// hon min-content cua no, nen tin dinh kem rong co dinh (trinh phat audio) day
// vo bubble tran ra ngoai cot.
// KHÔNG cấp cột riêng cho thanh công cụ. Đã thử và hỏng: cột 116px giữ chỗ ở
// MỌI tin đẩy bong bóng xa mép, để lại một khoảng rỗng suốt cuộc hội thoại kể
// cả khi không rê chuột — đổi "hàng tin không nhảy" lấy một khung chat trông
// lệch hẳn, quá đắt. Thanh công cụ nay nổi lên (absolute) nên lúc nghỉ khung
// chat y như trước.
const gridClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'grid grid-cols-[40px_minmax(0,1fr)]',
    // BonBoZ: phía mình không vẽ avatar nữa — ai gửi đã có trong tooltip. Cột
    // 20px còn lại chỉ để đặt dấu "đã gửi".
    [ORIENTATION.RIGHT]: 'grid grid-cols-[minmax(0,1fr)_20px]',
  };

  return map[orientation.value];
});

const gridTemplate = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: `
      "avatar bubble"
      "spacer meta"
    `,
    [ORIENTATION.RIGHT]: `
      "bubble tick"
      "meta spacer"
    `,
  };

  return map[orientation.value];
});

const shouldGroupWithNext = computed(() => {
  if (props.status === MESSAGE_STATUS.FAILED) return false;

  return props.groupWithNext;
});

// BonBoZ: chỉ khách có avatar. Bên mình ai cũng biết là mình, vẽ thêm một cột
// avatar chỉ tốn chỗ và làm hàng tin rối.
const shouldShowAvatar = computed(
  () =>
    props.messageType !== MESSAGE_TYPES.ACTIVITY &&
    orientation.value === ORIENTATION.LEFT
);

// BonBoZ: chỉ hai trạng thái được vẽ.
//
//   delivered -> dấu tick, đeo ở tin gửi đi cuối cùng
//   read      -> avatar khách, đeo ở mốc đọc (xem MessageList#lastReadOutgoingId)
//
// Không bao giờ hiện cả hai trên cùng một tin: avatar THAY dấu tick. Trạng thái
// `sent` cố ý để trống — Meta chưa báo gì thì mình cũng chưa biết gì.
//
// Tin lỗi và tin đang gửi phải tự loại ở đây: MessageMeta cũ giữ điều kiện đó,
// nay đã bỏ, thiếu nó thì tin gửi hỏng vẫn khoe dấu.
const coTheDeoDau = computed(
  () => !props.private && !props.contentAttributes?.deleted
);

const showReadAvatar = computed(
  () => coTheDeoDau.value && props.isReadWatermark
);

const showDeliveredTick = computed(() => {
  if (!coTheDeoDau.value) return false;
  if (showReadAvatar.value) return false;
  if (!props.isLastOutgoing) return false;

  return props.status === MESSAGE_STATUS.DELIVERED;
});

// Avatar khách ở cột bên PHẢI, ngoài bong bóng. Avatar người gửi luôn ở bên
// trái — lần trước gỡ tính năng này vì hai thứ đó bị lẫn vào nhau.
const readAvatarInfo = computed(() => ({
  name: props.readBy?.name || '',
  src: props.readBy?.src || '',
}));

const readTooltip = computed(() =>
  props.readBy?.name
    ? t('BONBOZ.MESSAGE.SEEN_BY', { name: props.readBy.name })
    : t('CHAT_LIST.MESSAGE_READ')
);

// BonBoZ: tin nhân viên gửi thẳng từ app Zalo trên điện thoại. Dấu này thay dấu
// "đã gửi" — tin đã về tới đây thì hiển nhiên là đã gửi — và đứng ngoài bong
// bóng ở đúng cột đó, thay cho dòng "📱 từ app Zalo" cũ nằm trong bong bóng.
const showZaloAppMark = computed(
  () => zaloMeta.value.fromZaloApp && orientation.value === ORIENTATION.RIGHT
);

// BonBoZ: dau "bot" nhin thay duoc, khong phai re chuot moi biet. Mau nen iris
// cua bong bong bot qua kin dao — nguoi truc khong nho bang mau, va ho can
// liec qua la biet tin nao may tra loi de con do noi dung bot noi voi khach.
// Dung o cot tick nhu dau "tu app Zalo" — bot chi nhan tin di nen luon o phai.
const showBotMark = computed(
  () =>
    variant.value === MESSAGE_VARIANTS.BOT &&
    orientation.value === ORIENTATION.RIGHT
);
const botMarkTooltip = computed(() => {
  const name = props.sender?.name;
  return name
    ? t('BONBOZ.TIN_NHAN.DO_BOT_GUI', { name })
    : t('CONVERSATION.BOT');
});

// BonBoZ: chip cam xuc.
//
// Chi ve EMOJI, khong ve ten. Cau noi khong phai luc nao cung doc duoc ten
// nguoi tha — luc do no gui uid tho (`7031036758676117476`), va mot day so 19
// chu so dap giua khung chat thi vo nghia voi nguoi truc. Ten day du nam trong
// chu thich khi re chuot, cho no.
// Nguồn chính là bảng `bonboz_cam_xuc_tins`. Tin CŨ (trước khi có bảng đó) vẫn
// phải hiện được, nên rơi về cách đọc ngược ghi chú nội bộ ngày xưa.
const camXuc = computed(() => {
  if (props.bonbozCamXuc.length) {
    return props.bonbozCamXuc.map((item, i) => ({
      id: `cx-${i}`,
      emoji: item.emoji,
      who: item.ten,
    }));
  }
  return props.zaloReactions;
});

const zaloReactionEmojis = computed(() => [
  ...new Set(camXuc.value.map(item => item.emoji)),
]);

const zaloReactionTooltip = computed(() =>
  camXuc.value.map(item => `${item.emoji} ${item.who}`).join('\n')
);

// Cau noi khong doc duoc ten thi gui UID TRAN — mot day 19 chu so. Danh dau lai
// de ve no ra dang ma so mo nhat chu khong gia vo do la mot cai ten: nhin la
// biet ngay "cau noi khong biet ai", khac han voi mot nguoi ten la day so.
const zaloReactionRows = computed(() =>
  camXuc.value.map(item => ({
    id: item.id,
    emoji: item.emoji,
    ten: item.who,
    laUid: /^\d{6,}$/.test(item.who),
  }))
);

// Dem CHI hien khi no noi them duoc dieu gi. Hai emoji khac nhau da la hai
// nguoi, de them so "2" ben canh la thua; nhung ba nguoi cung tha mot tim thi
// khong co dem la khong biet co ba.
const zaloReactionCount = computed(() =>
  camXuc.value.length > zaloReactionEmojis.value.length
    ? props.zaloReactions.length
    : null
);

const componentToRender = computed(() => {
  if (props.isEmailInbox && !props.private) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) return EmailBubble;
  }

  if (props.contentType === CONTENT_TYPES.INPUT_CSAT) {
    return CSATBubble;
  }

  if (
    [CONTENT_TYPES.INPUT_SELECT, CONTENT_TYPES.FORM].includes(props.contentType)
  ) {
    return FormBubble;
  }

  if (props.contentType === CONTENT_TYPES.VOICE_CALL) {
    return VoiceCallBubble;
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return EmailBubble;
  }

  if (props.contentAttributes?.isUnsupported) {
    return UnsupportedBubble;
  }

  if (props.contentAttributes.type === 'dyte') {
    return DyteBubble;
  }

  // BonBoZ: ghi chú quảng cáo của Facebook::AdsReferralService; ghi chú cũ chỉ
  // nhận ra được qua dòng Ad ID trong markdown.
  if (
    props.contentAttributes?.bonbozAd?.adId ||
    (props.private && /^Ad ID:\s*\d{6,25}\s*$/m.test(props.content ?? ''))
  ) {
    return AdCardBubble;
  }

  // BonBoZ: thẻ template Facebook — payload do builder giữ lại, text đã nằm sẵn
  // trong content nên bong bóng chữ vẫn đúng nếu chỗ này không nhận ra.
  if (props.contentAttributes?.fbTemplate?.elements?.length) {
    return FbTemplateCardBubble;
  }

  const instagramSharedTypes = [
    ATTACHMENT_TYPES.STORY_MENTION,
    ATTACHMENT_TYPES.IG_STORY,
    ATTACHMENT_TYPES.IG_STORY_REPLY,
    ATTACHMENT_TYPES.IG_POST,
  ];
  if (instagramSharedTypes.includes(props.contentAttributes.imageType)) {
    return InstagramStoryBubble;
  }

  if (Array.isArray(props.attachments) && props.attachments.length === 1) {
    const fileType = props.attachments[0].fileType;

    if (fileType === ATTACHMENT_TYPES.FALLBACK) return FallbackBubble;

    // BonBoZ: `messageContent` chứ không phải `props.content` — ảnh gửi từ app
    // Zalo chỉ có mỗi dòng chú thích làm nội dung, tính theo nội dung thô thì
    // nó rơi xuống bong bóng chữ kèm thẻ đính kèm thay vì hiện thành ảnh.
    if (!messageContent.value) {
      if (fileType === ATTACHMENT_TYPES.IMAGE) return ImageBubble;
      if (fileType === ATTACHMENT_TYPES.FILE) return FileBubble;
      if (fileType === ATTACHMENT_TYPES.AUDIO) return AudioBubble;
      if (fileType === ATTACHMENT_TYPES.VIDEO) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.IG_REEL) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.EMBED) return EmbedBubble;
      if (fileType === ATTACHMENT_TYPES.LOCATION) return LocationBubble;
    }
    // Attachment content is the name of the contact
    if (fileType === ATTACHMENT_TYPES.CONTACT) return ContactBubble;
  }

  return TextBubble;
});

const shouldShowContextMenu = computed(() => {
  return !props.contentAttributes?.isUnsupported;
});

const isBubble = computed(() => {
  return props.messageType !== MESSAGE_TYPES.ACTIVITY;
});

const isMessageDeleted = computed(() => {
  return props.contentAttributes?.deleted;
});

const payloadForContextMenu = computed(() => {
  return {
    id: props.id,
    content_attributes: props.contentAttributes,
    // Chép ra thứ đang nhìn thấy, không kèm chú thích của cầu nối.
    content: messageContent.value,
    conversation_id: props.conversationId,
  };
});

const contextMenuEnabledOptions = computed(() => {
  const hasText = !!messageContent.value;
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);

  const isOutgoing = props.messageType === MESSAGE_TYPES.OUTGOING;
  const isFailedOrProcessing =
    props.status === MESSAGE_STATUS.FAILED ||
    props.status === MESSAGE_STATUS.PROGRESS;

  return {
    copy: hasText,
    delete:
      (hasText || hasAttachments) &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    cannedResponse: isOutgoing && hasText && !isMessageDeleted.value,
    copyLink: !isFailedOrProcessing,
    translate: !isFailedOrProcessing && !isMessageDeleted.value && hasText,
    replyTo:
      !props.private &&
      props.inboxSupportsReplyTo.outgoing &&
      !isFailedOrProcessing,
    report:
      isOnChatwootCloud.value &&
      isCaptainMessage.value &&
      !isMessageDeleted.value,
  };
});

const shouldRenderMessage = computed(() => {
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);
  const isEmailContentType = props.contentType === CONTENT_TYPES.INCOMING_EMAIL;
  const isUnsupported = props.contentAttributes?.isUnsupported;
  const isAnIntegrationMessage =
    props.contentType === CONTENT_TYPES.INTEGRATIONS;
  const isFailedMessage = props.status === MESSAGE_STATUS.FAILED;
  const hasExternalError = !!props.contentAttributes?.externalError;

  return (
    hasAttachments ||
    props.content ||
    isEmailContentType ||
    isUnsupported ||
    isAnIntegrationMessage ||
    isFailedMessage ||
    hasExternalError
  );
});

function openContextMenu(e) {
  const shouldSkipContextMenu =
    e.target?.classList.contains('skip-context-menu') ||
    ['a', 'img'].includes(e.target?.tagName.toLowerCase());
  if (shouldSkipContextMenu || getSelection().toString()) {
    return;
  }

  e.preventDefault();
  if (e.type === 'contextmenu') {
    useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
  }
  contextMenuPosition.value = {
    x: e.pageX || e.clientX,
    y: e.pageY || e.clientY,
  };
  showContextMenu.value = true;
}

function closeContextMenu() {
  showContextMenu.value = false;
  contextMenuPosition.value = { x: null, y: null };
}

function handleReplyTo() {
  const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
  const { conversationId, id: replyTo } = props;

  LocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, props);
}

// BonBoZ: thanh công cụ hover.
//
// Thả cảm xúc chỉ mở ở kênh nào NỀN TẢNG nhận được: Zalo qua cầu nối, Facebook
// và Instagram qua `sender_action: REACT` của Send API. Kênh khác thì ẩn nút
// luôn — nút bấm vào báo lỗi còn khó chịu hơn không có nút.
const KENH_CO_CAM_XUC = [
  INBOX_TYPES.API,
  INBOX_TYPES.FB,
  INBOX_TYPES.INSTAGRAM,
];

// `private` là từ khoá dành riêng nên không viết thẳng trong template được.
const laGhiChuNoiBo = computed(() => props.private);

// Tin Zalo nhập từ Pancake mang `source_id` dạng `pzl_m_...` — mã của Pancake
// chứ không phải msgId của Zalo, nên cầu nối tra không ra. Ẩn nút thay vì để
// nhân viên bấm rồi nhận lỗi. Cùng một luật với `ThaCamXucService::TIEN_TO_PANCAKE`.
const laTinNhapTuPancake = computed(() =>
  String(props.sourceId ?? '').startsWith('pzl_')
);

const choPhepCamXuc = computed(
  () =>
    KENH_CO_CAM_XUC.includes(inbox.value?.channel_type) &&
    !props.private &&
    !props.contentAttributes?.deleted &&
    Boolean(props.sourceId) &&
    !laTinNhapTuPancake.value
);

// Cảm xúc của CHÍNH người trực đang đăng nhập — để nút đổi thành "gỡ".
const camXucCuaMinh = computed(() =>
  props.bonbozCamXuc.find(item => item.nguoiLoai === 'User')
);

async function thaCamXuc(emoji) {
  try {
    await MessageApi.thaCamXuc(props.conversationId, props.id, emoji);
  } catch (error) {
    useAlert(error?.response?.data?.error || t('BONBOZ.TIN_NHAN.THA_CAM_XUC'));
  }
}

async function goCamXuc() {
  try {
    await MessageApi.goCamXuc(props.conversationId, props.id);
  } catch (error) {
    useAlert(error?.response?.data?.error || t('BONBOZ.TIN_NHAN.GO_CAM_XUC'));
  }
}

function moChuyenTiep() {
  emitter.emit(BUS_EVENTS.BONBOZ_MO_CHUYEN_TIEP, {
    conversationId: props.conversationId,
    messageId: props.id,
  });
}

const avatarInfo = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    const { name, avatar_url, channel_type, medium, voice_enabled } =
      inbox.value;
    const iconName = avatar_url
      ? null
      : getInboxIconByType(channel_type, medium, 'fill', voice_enabled);
    return {
      name: iconName ? '' : name || t('CONVERSATION.NATIVE_APP'),
      src: avatar_url || '',
      iconName,
    };
  }

  // If no sender, check for Slack (or other integration) sender info
  if (!props.sender) {
    const { senderName, senderAvatarUrl } = props.additionalAttributes || {};
    if (senderName) {
      return { name: senderName, src: senderAvatarUrl ?? '' };
    }
    // BonBoZ: chi tu xung "Bot" khi tin THAT SU do bot tu ghi lai
    // (`bonboz_bot`). Tin thieu sender vi ly do khac thi noi thang la khong
    // ro — mao nhan bot lam nguoi truc het tin vao nhan.
    if (props.contentAttributes?.bonbozBot) {
      return { name: t('CONVERSATION.BOT'), src: '' };
    }
    return { name: t('BONBOZ.TIN_NHAN.KHONG_RO_NGUOI_GUI'), src: '' };
  }

  // BonBoZ: trong nhóm Zalo, liên hệ là cả cái nhóm nên ảnh liên hệ lặp y hệt
  // trên mọi bong bóng và không nói được ai vừa nói. Có tên người nói thì vẽ
  // chữ cái đầu của họ — mỗi người một màu, đúng kiểu nhóm chat Facebook.
  if (speakerName.value) {
    return { name: speakerName.value, src: '' };
  }

  const { sender } = props;
  const { name, type, avatarUrl, thumbnail } = sender || {};

  // If sender type is agent bot, use avatarUrl
  if ([SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(type)) {
    return {
      name: name ?? '',
      src: avatarUrl ?? '',
    };
  }

  // For all other senders, use thumbnail
  return {
    name: name ?? '',
    src: thumbnail ?? '',
  };
});

// BonBoZ: tên người gửi + giờ gửi chuyển hẳn vào tooltip. Hai dòng, giờ 24 tiếng
// rồi ngày/tháng — đúng thứ tự người Việt đọc. Không ép múi giờ: máy nhân viên
// đã ở giờ Việt Nam, ép thêm chỉ tạo cơ hội sai.
// BonBoZ: tren man cam ung khong ro chuot duoc, ma gio gui tin va ten nguoi noi
// chi hien qua `:hover` — nghia la KHONG CO CACH NAO xem duoc. Cham vao bong
// bong de bat/tat chu thich; may tinh giu nguyen loi ro chuot.
//
// KHONG dung `useWindowSize` o day: component nay duoc dung cho TUNG tin nhan,
// mot hoi thoai vai tram tin la vai tram bo lang nghe resize. Viec phan biet
// khổ man de cho CSS lo (xem `@media` cuoi file).
const chuThichDangHien = ref(false);

// Nhan giu 500ms mo menu ngu canh — tren iPhone day la duong DUY NHAT toi
// Tra loi / Sao chep / Dich / Xoa. Xem `composables/useNhanGiu.js`.
const {
  batDau: batDauGiu,
  diChuyen: diChuyenGiu,
  huy: huyGiu,
  boQuaCuBam,
} = useNhanGiu(su => openContextMenu(su));

const batTatChuThich = su => {
  // Vua giu lau xong thi cu `click` di kem khong duoc bat chu thich nua.
  if (boQuaCuBam()) return;
  // Bam vao lien ket, nut, anh, video... la thao tac that cua nguoi dung —
  // khong cuop lay de bat chu thich.
  if (
    su.target?.closest?.(
      'a, button, img, video, input, textarea, [role="button"]'
    )
  )
    return;
  chuThichDangHien.value = !chuThichDangHien.value;
};

const bubbleTooltip = computed(() => {
  // Dòng hoạt động của hệ thống không có người gửi nên không cần tooltip.
  if (props.variant === MESSAGE_VARIANTS.ACTIVITY) return '';

  const time = dateFormat(props.createdAt, 'HH:mm dd/MM');
  // Trong hội thoại nhóm, liên hệ chính là cái nhóm nên tên liên hệ không nói
  // được ai vừa nhắn. Cầu nối Zalo gửi kèm tên người nói thật sự; tin không có
  // tên người nói vẫn rơi về tên liên hệ như trước.
  const name = speakerName.value || avatarInfo.value.name;

  return name ? `${name}\n${time}` : time;
});

const setupHighlightTimer = () => {
  if (Number(route.query.messageId) !== Number(props.id)) {
    return;
  }

  showBackgroundHighlight.value = true;
  const HIGHLIGHT_TIMER = 1000;
  useTimeoutFn(() => {
    showBackgroundHighlight.value = false;
  }, HIGHLIGHT_TIMER);
};

onMounted(setupHighlightTimer);

provideMessageContext({
  ...toRefs(props),
  content: messageContent,
  isPrivate: computed(() => props.private),
  variant,
  orientation,
  isBotOrAgentMessage,
  shouldGroupWithNext,
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="shouldRenderMessage"
    :id="`message${props.id}`"
    class="group relative flex w-full message-bubble-container"
    :data-message-id="props.id"
    :class="[
      flexOrientationClass,
      // Chip cam xuc tran xuong duoi bong bong (-bottom-2 = 8px) va cao ~18px,
      // an het khoang mb-2 roi de len hang tin ke tiep. Tin co cam xuc thi gian
      // hang ra — Messenger cung gian dong nhu vay khi co reaction.
      zaloReactionEmojis.length ? 'mb-4' : 'mb-2',
      {
        'hien-chu-thich': chuThichDangHien,
        'group-with-next': shouldGroupWithNext,
        'bg-n-alpha-1': showBackgroundHighlight,
      },
    ]"
  >
    <div v-if="variant === MESSAGE_VARIANTS.ACTIVITY">
      <ZaloKetBanCard v-if="contentAttributes?.bonbozZaloKetBan" />
      <ZaloHuyKetBanCard v-else-if="contentAttributes?.bonbozZaloHuyKetBan" />
      <ZaloCuocGoiCard v-else-if="contentAttributes?.bonbozZaloCuocGoi" />
      <ActivityBubble v-else :content="content" />
    </div>
    <div
      v-else
      :class="[
        gridClass,
        {
          'gap-y-2': contentAttributes.externalError,
          'w-full': variant === MESSAGE_VARIANTS.EMAIL,
        },
      ]"
      class="gap-x-2"
      :style="{
        gridTemplateAreas: gridTemplate,
      }"
    >
      <div
        v-if="!shouldGroupWithNext && shouldShowAvatar"
        class="[grid-area:avatar] flex items-end"
      >
        <!-- BonBoZ: avatar canh đáy, shadow theo Pancake; cỡ co theo màn hình. -->
        <Avatar
          v-bind="avatarInfo"
          :size="messageAvatarSize"
          rounded-full
          class="rounded-full shadow-[0_1px_2px_rgba(0,0,0,0.38)]"
        />
      </div>
      <!-- BonBoZ: `items-center` canh thanh công cụ vào giữa bubble. Lề 2rem là
           khoảng thở với mép đối diện, không liên quan tới lane. -->
      <div
        class="[grid-area:bubble] relative flex items-center min-w-0"
        :class="{
          'ltr:ml-8 rtl:mr-8 justify-end bonboz-lan-trai':
            orientation === ORIENTATION.RIGHT,
          'ltr:mr-8 rtl:ml-8 bonboz-lan-phai': orientation === ORIENTATION.LEFT,
        }"
        @contextmenu="openContextMenu($event)"
        @click="batTatChuThich($event)"
        @touchstart.passive="batDauGiu"
        @touchmove.passive="diChuyenGiu"
        @touchend="huyGiu"
        @touchcancel="huyGiu"
      >
        <!-- BonBoZ: lop boc `w-fit` OM SAT bong bong de chip cam xuc va chu
             thich gio neo theo mep bong bong THAT. Truoc day chung neo vao ca
             track grid: phia phai co justify-end nen mep phai trung bong bong
             (nhin on), nhung phia trai thi mep phai track cach xa bong bong
             ngan — chip danh phai nam goc duoi-TRAI, lech han kieu goc
             duoi-PHAI cua Messenger/Zalo. Email la ngoai le: bubble email an
             het be ngang nen giu w-full. -->
        <div
          class="relative min-w-0 max-w-full"
          :class="variant === MESSAGE_VARIANTS.EMAIL ? 'w-full' : 'w-fit'"
        >
          <Component :is="componentToRender" />
          <!-- BonBoZ: chip dinh vao MEP DUOI bong bong, kieu Zalo/Messenger.
             Truoc day moi lan tha cam xuc la mot bong bong thong bao rieng, ben
             tren con kep ca ban trich dan cua tin goc — mot hanh dong mot cham
             phinh thanh khoi to hon chinh tin nhan, nhom dong nguoi thi lap kin
             ca cuoc tro chuyen.
             `absolute` nen chip khong lam bong bong cao them.
             Chip QUAY VAO GIUA khung chat: tin khach (bong bong ben trai) thi
             chip ben PHAI, tin minh (ben phai) thi chip ben TRAI. Dat ca hai ve
             mot phia thi chip cua tin minh bi day ra sat mep man hinh. -->
          <div
            v-if="zaloReactionEmojis.length"
            class="absolute -bottom-2 z-10 group/cx"
            :class="
              orientation === ORIENTATION.RIGHT
                ? 'ltr:left-2 rtl:right-2'
                : 'ltr:right-2 rtl:left-2'
            "
          >
            <div
              :aria-label="zaloReactionTooltip"
              class="flex gap-0.5 items-center px-1.5 py-px text-xs leading-none rounded-full border shadow-sm cursor-default select-none border-n-weak bg-n-solid-1"
            >
              <span v-for="emoji in zaloReactionEmojis" :key="emoji">
                {{ emoji }}
              </span>
              <span v-if="zaloReactionCount" class="text-n-slate-11">
                {{ zaloReactionCount }}
              </span>
            </div>
            <!-- Ai da tha, hien khi re chuot vao chip.
               KHONG dung `title` cua trinh duyet: no doi gan mot giay moi hien,
               ve theo giao dien he dieu hanh chu khong theo theme, va khong xuong
               dong duoc cho tung nguoi mot.
               Cung KHONG dung tooltip cua floating-vue: no teleport ra `<body>`
               nen khong thuoc khung chat — cuon thi no dung yen roi troi de len
               khung go, va khi no phu len vung re chuot thi sinh vong hien/an lien
               tuc. Day cung la ly do ten nguoi gui o `ThanhCongCuTin` tu ve
               trong dong; lam giong vay cho nhat quan.
               Xo XUONG DUOI chu khong len tren: chip da nam o mep duoi bong bong,
               hien len tren la che mat dung cai tin vua duoc tha cam xuc. -->
            <div
              class="hidden absolute top-full z-20 px-2 py-1 mt-1 text-xs rounded-md border shadow-lg pointer-events-none group-hover/cx:block w-max max-w-56 border-n-weak bg-n-solid-3 text-n-slate-12"
              :class="
                orientation === ORIENTATION.RIGHT
                  ? 'ltr:right-0 rtl:left-0'
                  : 'ltr:left-0 rtl:right-0'
              "
            >
              <div
                v-for="row in zaloReactionRows"
                :key="row.id"
                class="flex gap-1.5 items-center py-px"
              >
                <span class="text-sm leading-none shrink-0">{{
                  row.emoji
                }}</span>
                <span
                  class="truncate"
                  :class="
                    row.laUid
                      ? 'font-mono text-[11px] text-n-slate-11'
                      : 'text-n-slate-12'
                  "
                >
                  {{ row.ten }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <!-- BonBoZ: thanh công cụ nằm TRONG DÒNG, cạnh bubble, không `absolute`.
             Nhờ vậy lane rộng đúng bằng nội dung của nó: tên ngắn thì lane hẹp
             và bubble được rộng ra, tên dài thì lane tự nới. Không còn con số
             nào phải đoán, cũng không còn cảnh tên bị cắt vì lane cố định.
             `shrink-0` để bubble (`min-w-0`) là bên nhường chỗ khi chật.
             Lúc nghỉ vẫn chiếm chỗ (chỉ `opacity-0`) nên hiện ra không làm hàng
             tin nhảy. -->
        <ThanhCongCuTin
          v-if="
            variant !== MESSAGE_VARIANTS.ACTIVITY && !contentAttributes?.deleted
          "
          class="z-20 shrink-0"
          :class="
            orientation === ORIENTATION.RIGHT
              ? 'order-first ltr:mr-1.5 rtl:ml-1.5'
              : 'ltr:ml-1.5 rtl:mr-1.5'
          "
          :chu-thich="bubbleTooltip"
          :cho-phep-tra-loi="inboxSupportsReplyTo.outgoing && !laGhiChuNoiBo"
          :cho-phep-cam-xuc="choPhepCamXuc"
          :da-tha-cam-xuc="Boolean(camXucCuaMinh)"
          :phia="orientation === ORIENTATION.RIGHT ? 'left' : 'right'"
          @tra-loi="handleReplyTo"
          @chuyen-tiep="moChuyenTiep"
          @cam-xuc="thaCamXuc"
          @go-cam-xuc="goCamXuc"
          @mo-menu="openContextMenu"
        />
      </div>
      <div
        v-if="
          showBotMark || showZaloAppMark || showDeliveredTick || showReadAvatar
        "
        class="[grid-area:tick] flex items-end pb-1"
      >
        <!-- Cot tick chi rong 20px nen moi tin deo dung MOT dau. Uu tien:
             "khach da xem" > "tu app Zalo" > "bot" > "da nhan".
             Avatar dung dau vi khong con duong nao khac de biet khach da xem;
             con tin bot / tin tu app Zalo thi mau bong bong da noi len roi.
             Dau bot va app Zalo thay duoc dau "da nhan" — tin da nam day thi
             hien nhien la da gui di. -->
        <Avatar
          v-if="showReadAvatar"
          v-tooltip.top-start="readTooltip"
          v-bind="readAvatarInfo"
          :size="17"
          rounded-full
          class="rounded-full ring-1 ring-n-background"
        />
        <Icon
          v-else-if="showZaloAppMark"
          v-tooltip.top-start="t('BONBOZ.MESSAGE.FROM_ZALO_APP')"
          icon="i-lucide-smartphone"
          class="size-[14px] text-n-slate-10"
        />
        <Icon
          v-else-if="showBotMark"
          v-tooltip.top-start="botMarkTooltip"
          icon="i-lucide-bot"
          class="size-[14px] text-n-slate-10"
        />
        <Icon
          v-else-if="showDeliveredTick"
          v-tooltip.top-start="t('BONBOZ.MESSAGE.DELIVERED')"
          icon="i-lucide-check"
          class="size-[14px] text-n-slate-10"
        />
      </div>
      <MessageError
        v-if="contentAttributes.externalError"
        class="[grid-area:meta]"
        :class="flexOrientationClass"
        :error="contentAttributes.externalError"
        @retry="emit('retry')"
      />
    </div>
    <div v-if="shouldShowContextMenu" class="context-menu-wrap">
      <ContextMenu
        v-if="isBubble"
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :enabled-options="contextMenuEnabledOptions"
        :message="payloadForContextMenu"
        hide-button
        @open="openContextMenu"
        @close="closeContextMenu"
        @reply-to="handleReplyTo"
        @chuyen-tiep="moChuyenTiep"
      />
    </div>
  </div>
</template>

<style lang="scss">
// BonBoZ: tên người gửi + giờ, render trong `ThanhCongCuTin`.
//
// Style đặt ở đây, không ở `<style scoped>` của component đó: hai nơi cùng
// specificity nên thứ tự import quyết định bên nào thắng. Một file thì thứ tự
// là thứ tự đọc.
//
// MỘT dòng, `leading-none`. Cụm xếp dọc cao 49px (tên 11 + gap 2 + viên thuốc
// 36) so với bubble một dòng 44px, nên hàng tin giãn 5px — giá phải trả cho
// icon to. Để tên wrap 2 dòng thì thành 60px, giãn gấp ba.
// `max-w` chỉ là trần chống tên dài vô lý, không phải bề rộng lane.
// `truncate` kéo theo `nowrap`, nuốt luôn `\n` trong chuỗi `Tên\nGiờ`.
// Chữ nhỏ thì phải tăng weight bù lại mới đọc được.
.bonboz-ten-lan {
  @apply text-[11px] font-semibold leading-none truncate select-text max-w-[14rem] text-n-slate-12;
}

// ===========================================================================
// BonBoZ: khung chat quá hẹp thì thanh công cụ rơi xuống dưới bubble.
// Container khai báo ở `MessageList.vue`.
//
// Ở thế rộng KHÔNG có luật nào ở đây: thanh công cụ nằm trong dòng nên lane tự
// rộng đúng bằng nội dung. Ngưỡng dưới đây chỉ quyết định KHI NÀO đổi thế, chứ
// không quyết định lane rộng bao nhiêu — không có con số nào bị đoán.
//
// 26rem: dưới mức này, thanh công cụ (~200px) ăn hết chỗ và bubble teo lại còn
// vài chục px.
// ===========================================================================
@container khung-chat (max-width: 26rem) {
  // Bỏ khỏi dòng, treo xuống dưới bubble. `absolute` nên không còn ép bubble.
  // Class lặp để thắng `shrink-0`, `order-first`, `ltr:ml-1.5`... đặt inline.
  .bonboz-thanh-cong-cu.bonboz-thanh-cong-cu {
    position: absolute;
    inset-inline: auto;
    top: 100%;
    margin: 0.25rem 0 0;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }

  // Giữ luật trái/phải: tin khách bám mép trái, tin mình bám mép phải. Viên
  // thuốc luôn sát bubble nhất, tên ở ngoài.
  .bonboz-lan-phai .bonboz-thanh-cong-cu.bonboz-thanh-cong-cu {
    inset-inline-start: 0;
  }

  .bonboz-lan-trai .bonboz-thanh-cong-cu.bonboz-thanh-cong-cu {
    inset-inline-end: 0;
    flex-direction: row-reverse;
  }

  // Dưới bubble thì rộng rãi, cho tên dài ra.
  .bonboz-ten-lan {
    max-width: 14rem;
  }

  // `absolute` thì không chiếm chỗ nữa, để nguyên sẽ đè lên row kế tiếp. Chừa
  // sẵn đúng chiều cao của nó (viên thuốc 36px + gap 4px). Class lặp để thắng
  // `mb-2`/`mb-4` đặt inline.
  //
  // `hover: hover` loại màn cảm ứng: ở đó `:hover` dính lại sau khi chạm, chạm
  // lần hai gỡ class nhưng `:hover` chiếm chỗ tiếp.
  @media (hover: hover) {
    .message-bubble-container:hover {
      margin-bottom: 2.75rem;
    }
  }

  // Cung chi cho man cam ung, cung ly do voi luat `.hien-chu-thich` o cuoi file.
  @media (hover: none) {
    .message-bubble-container.hien-chu-thich {
      margin-bottom: 2.75rem;
    }
  }
}

// BonBoZ: RE CHUOT hien thanh cong cu — chi tren thiet bi co ro chuot that.
//
// Truoc day luat nay la hai lop Tailwind `group-hover:` dat thang tren the
// `ThanhCongCuTin`. Khong sai tren may tinh, nhung Tailwind khong cho boc bien
// the `group-hover:` trong media query, nen no chay ca tren dien thoai — noi
// `:hover` DINH LAI sau khi cham va chi nha ra khi cham vao cho khac.
//
// Hau qua: cham lan hai de tat thi lop `hien-chu-thich` bi go (hang tin thu lai
// dung khoang da chua), nhung `:hover` van con nen thanh cong cu KHONG an di —
// ten va gio de len tin ke tiep (bao loi 23/09/2026).
@media (hover: hover) {
  .message-bubble-container:hover .bonboz-thanh-cong-cu {
    @apply opacity-100 pointer-events-auto;
  }
}

// BonBoZ: CHI cho man cam ung. O do khong co ro chuot, nen cham vao bong bong
// la cach duy nhat de voi toi tra loi / chuyen tiep / tha cam xuc.
//
// `hover: none` la bat buoc. Thieu no thi tren may tinh, bam mot cai la thanh
// cong cu DINH lai: ro chuot di cho khac van thay, phai bam lan nua moi tat.
// Dung chuot thi ro vao hien, ro ra tat — khong co trang thai nao phai nho.
@media (hover: none) {
  .hien-chu-thich .bonboz-thanh-cong-cu {
    @apply opacity-100 pointer-events-auto;
  }
}

// BonBoZ: ban phim van phai toi duoc thanh cong cu. Dung `:has(:focus-visible)`
// chu khong phai `focus-within`: `focus-within` bat ca khi BAM CHUOT vao nut,
// nen bam xong la thanh cong cu dinh lai du da ro chuot di cho khac.
// `:focus-visible` chi sang khi dieu huong bang ban phim.
.bonboz-thanh-cong-cu:has(:focus-visible) {
  @apply opacity-100 pointer-events-auto;
}

// BonBoZ: góc dính tin kề cùng cụm là 4px, không phải rounded-sm (2px).
.group-with-next + .message-bubble-container {
  .left-bubble {
    @apply ltr:rounded-tl-[4px] rtl:rounded-tr-[4px];
  }

  .right-bubble {
    @apply ltr:rounded-tr-[4px] rtl:rounded-tl-[4px];
  }
}
</style>
